import React, { useState } from "react";
import Card from "./card";
import { connect } from 'react-redux';
import { fetchYelpData } from "../api/actions/query";
import { downloadYelpData } from "../api/actions/mutation";
import { 
  Typography, 
  Toolbar, 
  IconButton, 
  Button, 
  Tooltip, 
  Menu, 
  MenuItem,
  CircularProgress,
  Box,
  Pagination
} from "@mui/material";
import ArrowBack from "@mui/icons-material/ArrowBack";
import { Download } from "@mui/icons-material";
import { useHistory } from "react-router-dom";
import SectionPaper from "../../common/SectionPaper";
import { messageShow } from "../../common/api/actions";

const Yelp = ({ messageShow }) => {
    const [location, setLocation] = useState("");
    const [term, setTerm] = useState("");
    const [results, setResults] = useState({});
    const [totalResults, setTotalResults] = useState(0);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [page, setPage] = useState(1);
    const [anchorEl, setAnchorEl] = useState(null);
    const limit = 50;
    const history = useHistory();

    const handleSearch = async () => {
        setError(null);
        setResults({});
        setIsLoading(true);
        setTotalResults(0);
        setPage(1);

        if (!location || !term) {
            setError("Please enter both location and search term");
            setIsLoading(false);
            return;
        }

        try {
            const response = await fetchYelpData(location, term, 0, limit);
            const data = response.data;
            setTotalResults(data.total);
            setResults({ 1: data.businesses });
        } catch (err) {
            console.error(err);
            setError("Failed to fetch Yelp data");
        } finally {
            setIsLoading(false);
        }
    };

    const handlePageChange = async (event, newPage) => {
        if (results[newPage]) {
            setPage(newPage);
            return;
        }

        setIsLoading(true);
        try {
            const offset = (newPage - 1) * limit;
            const response = await fetchYelpData(location, term, offset, limit);
            setResults(prev => ({
                ...prev,
                [newPage]: response.data.businesses
            }));
            setPage(newPage);
        } catch (err) {
            setError("Failed to load page");
        } finally {
            setIsLoading(false);
        }
    };

    const handleMenuClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const onDownloadCurrentPage = async () => {
        handleMenuClose();
        setIsDownloading(true);
        
        try {
            const response = await downloadYelpData({
                businesses: results[page],
                location,
                term,
                isComplete: false,
                page
            });

            if (response.data.success) {
                messageShow("Current page download completed successfully");
            } else {
                messageShow("Download failed");
            }
        } catch (error) {
            console.error("Download error:", error);
            messageShow("Download error occurred");
        } finally {
            setIsDownloading(false);
        }
    };

const onDownloadAllResults = async () => {
    handleMenuClose();
    setIsDownloading(true);

    try {
        const allCachedBusinesses = Object.values(results).flat();
        const response = await downloadYelpData({
            businesses: allCachedBusinesses,
            location,
            term,
            isComplete: false,
            totalDownloaded: allCachedBusinesses.length,
        });

        if (response.data.success) {
            messageShow(`Successfully downloaded ${allCachedBusinesses.length} results`);
        } else {
            messageShow("Download failed");
        }
    } catch (error) {
        console.error("Download error:", error);
        messageShow("Download error occurred");
    } finally {
        setIsDownloading(false);
    }
};


    return (
        <div>
            <Toolbar>
                <IconButton
                    color="inherit"
                    onClick={() => history.push("/crm")}
                    edge="start"
                >
                    <ArrowBack />
                </IconButton>
                <Typography variant="h6" color="inherit" style={{ marginLeft: 16 }}>
                    Yelp Search
                </Typography>
            </Toolbar>

            <SectionPaper>
                <Box p={3}>
                    <Box display="flex" gap={2} mb={3}>
                        <input
                            type="text"
                            placeholder="Enter category (e.g., restaurants)"
                            value={term}
                            onChange={(e) => setTerm(e.target.value)}
                            style={{
                                padding: "10px",
                                width: "300px",
                                borderRadius: "4px",
                                border: "1px solid #ccc"
                            }}
                        />
                        <input
                            type="text"
                            placeholder="Enter location (e.g., New York)"
                            value={location}
                            onChange={(e) => setLocation(e.target.value)}
                            style={{
                                padding: "10px",
                                width: "300px",
                                borderRadius: "4px",
                                border: "1px solid #ccc"
                            }}
                        />
                        <Button 
                            variant="contained" 
                            onClick={handleSearch}
                            disabled={isLoading}
                        >
                            {isLoading ? "Searching..." : "Search"}
                        </Button>
                    </Box>

                    {error && (
                        <Typography color="error" mb={2}>
                            {error}
                        </Typography>
                    )}

                    {results[page]?.length > 0 && (
                        <>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                <Typography variant="h6">
                                    Search Results: ({totalResults} total)
                                </Typography>
                                
                                <Tooltip title="Download Options">
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        onClick={handleMenuClick}
                                        startIcon={<Download />}
                                        disabled={isDownloading}
                                    >
                                        {isDownloading ? <CircularProgress size={24} /> : "Download"}
                                    </Button>
                                </Tooltip>

                                <Menu
                                    anchorEl={anchorEl}
                                    open={Boolean(anchorEl)}
                                    onClose={handleMenuClose}
                                >
                                    <MenuItem onClick={onDownloadCurrentPage}>
                                        <Download style={{ marginRight: 10 }} />
                                        Download Current Page
                                    </MenuItem>
                                    <MenuItem onClick={onDownloadAllResults}>
                                        <Download style={{ marginRight: 10 }} />
                                        Download All Results
                                    </MenuItem>
                                </Menu>
                            </Box>

                            <Box display="flex" flexWrap="wrap" gap={2}>
                                {results[page]?.map((business, index) => (
                                    <Card key={business.id} business={business} index={(page - 1) * limit + index} messageShow={messageShow}/>
                                ))}
                            </Box>
                            <Box display="flex" justifyContent="center" mt={3}>
                                <Pagination 
                                    count={Math.ceil(totalResults / limit)}
                                    page={page}
                                    onChange={handlePageChange}
                                    color="primary"
                                    disabled={isLoading}
                                />
                            </Box>
                        </>
                    )}
                </Box>
            </SectionPaper>
        </div>
    );
};

export default connect(null, { 
    fetchYelpData,
    downloadYelpData,
    messageShow 
})(Yelp);
